/* eslint-disable complexity */
import { useEffect, useMemo, useState } from 'react';
import {
  debounce,
  switchMap,
  fromPromise,
  makeSubject,
  pipe,
  subscribe,
  onPush,
  fromValue,
} from 'wonka';
import { ModelsMap, useViewer } from '../components/common/ForgeViewer';

export type SearchResultMap = Map<
  string,
  {
    resolvedQueryString: string;
    dbIds: Map<string, Set<number>>;
    count: number;
    propertyCategory: string;
    propertyName: string;
    propertyValue: string;
  }
>;

type UseViwerSearchReturn = {
  loading: boolean;
  search: (query: string | undefined) => void;
  searchResults: SearchResultMap;
};

// Use a very seldomly used character as a separator, like the unicorn emoji character
const RESULT_GROUP_SEPARATOR = '🦄';

const CATEGORY_BLACKLIST: Array<string | RegExp> = [
  'Item',
  'Element ID',
  'Revit Type',
  'Other',
];
const PROPERTY_BLACKLIST: Array<string | RegExp> = [
  /^Category ID/i,
  /^IfcContainedInHostGUID/i,
  /^IfcContainedInHost/i,
  /^IfcPropertySetList/i,
  /^IFcSpatialContainerGUID/i,
  /^Export Type to IFC/i,
  /^Type IfcGUID/i,
  /^Type IfcPropertySetList/i,
  /^Export To IFC As/i,
];

const LITERAL_QUERY_PATTERN = /^"(?<query>.*)"$/;

const isBlackListed = (value: string, blacklist: Array<string | RegExp>) =>
  blacklist.some((item) =>
    item instanceof RegExp
      ? item.test(value)
      : item.toLowerCase() === value.toLowerCase()
  );

const searchInModels = async (
  models: ModelsMap,
  query: string
): Promise<SearchResultMap> => {
  const literalQueryMatch = query.match(LITERAL_QUERY_PATTERN);
  let resolvedQueryString: string;
  let useLiteralMatching: boolean;

  if (literalQueryMatch && literalQueryMatch.groups) {
    resolvedQueryString = literalQueryMatch.groups['query'];
    useLiteralMatching = true;
  } else {
    resolvedQueryString = query.toLowerCase();
    useLiteralMatching = false;
  }

  const results: SearchResultMap = new Map();

  for (const [modelUrn, model] of Object.entries(models)) {
    const matchingDbIds: number[] = await new Promise((resolve, reject) => {
      model.search(
        query,
        (dbIds: number[]) => {
          resolve(dbIds);
        },
        (error: Error) => {
          reject(error);
        }
      );
    });

    const elements: Autodesk.Viewing.PropertyResult[] = await new Promise(
      (resolve, reject) => {
        model.getBulkProperties2(
          matchingDbIds,
          {
            categoryFilter: undefined,
            propFilter: undefined,
            ignoreHidden: true,
          },
          (propertyResult) => {
            resolve(propertyResult);
          },
          (error) => {
            reject(error);
          }
        );
      }
    );

    for (const element of elements) {
      if (
        // NOTE: I don't think this is a bottleneck but it could _maybe_ be simplified to only check once of these
        !element.properties.some((prop) => prop.displayName === 'IfcName') ||
        false
        // Swap in the following _maybe_
        // !element.properties.some((prop) => prop.displayName === 'IfcClass')
      ) {
        continue;
      }

      for (const property of element.properties) {
        if (
          isBlackListed(property.displayCategory, CATEGORY_BLACKLIST) ||
          isBlackListed(property.displayName, PROPERTY_BLACKLIST)
        ) {
          continue;
        }

        const resultKey = `${property.displayCategory}${RESULT_GROUP_SEPARATOR}${property.displayName}${RESULT_GROUP_SEPARATOR}${property.displayValue}`;

        if (results.has(resultKey)) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const result = results.get(resultKey)!;

          if (result.dbIds.has(modelUrn)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            result.dbIds.get(modelUrn)!.add(element.dbId);
          } else {
            result.dbIds.set(modelUrn, new Set([element.dbId]));
          }
          result.count++;
        } else {
          let searchCandidates = [
            `${property.displayValue}`,
            property.displayName,
            property.displayCategory,
          ];

          let isMatchingSearchQuery = false;
          if (useLiteralMatching) {
            isMatchingSearchQuery = searchCandidates.some(
              (candidate) => candidate === resolvedQueryString
            );
          } else {
            isMatchingSearchQuery = searchCandidates.some((value) =>
              value.toLocaleLowerCase().includes(resolvedQueryString)
            );
          }

          if (isMatchingSearchQuery) {
            results.set(resultKey, {
              resolvedQueryString,
              dbIds: new Map([[modelUrn, new Set([element.dbId])]]),
              count: 1,
              propertyCategory: property.displayCategory,
              propertyName: property.displayName,
              propertyValue: `${property.displayValue}`,
            });
          }
        }
      }
    }
  }

  return results;
};

export const useViewerSearch = (): UseViwerSearchReturn => {
  const [searchResults, setSearchResults] = useState<SearchResultMap>(
    new Map()
  );
  const [loading, setLoading] = useState(false);
  const { isAllModelsLoaded, loadedModels } = useViewer();

  const searchSubject = useMemo(() => makeSubject<string | undefined>(), []);

  useEffect(() => {
    if (isAllModelsLoaded) {
      const { unsubscribe } = pipe(
        searchSubject.source,
        debounce((query: string | undefined) =>
          query && query.length > 0 ? 700 : 0
        ),
        onPush((query: string | undefined) => {
          if (typeof query !== 'undefined' && query.length > 0) {
            // This way we don't set loading if we don't intend to actually search for anything.
            setLoading(true);
          }
        }),
        switchMap((query) => {
          if (typeof query === 'undefined' || query.length === 0) {
            return fromValue(new Map());
          } else {
            return fromPromise(searchInModels(loadedModels, query));
          }
        }),

        subscribe((result) => {
          setLoading(false);
          setSearchResults(result);
        })
      );
      return unsubscribe;
    }
  }, [loadedModels, isAllModelsLoaded, searchSubject.source]);

  return useMemo(
    () =>
      ({
        loading,
        search: (query: string) => {
          searchSubject.next(query);
        },
        searchResults,
      } as UseViwerSearchReturn),
    [loading, searchResults, searchSubject]
  );
};
